import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { routingGuidesSchema } from "~/shared/schemas/routing-guide-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	getAndValidate,
	getAndValidateTraxAr,
} from "~/shared/services/api/fetch-client.ts";

const getCustomerRoutingGuides = async (
	customerId: number,
	isInternal: boolean,
	signal?: AbortSignal,
) => {
	const signalOptions = signal ? { signal } : undefined;
	const url = isInternal
		? apiRoutes.GETCUSTOMERROUTINGGUIDES.replace(
				":customerId",
				customerId.toString(),
			)
		: apiRoutes.GETEXTERNALCUSTOMERROUTINGGUIDES;
	const resp = isInternal
		? await getAndValidateTraxAr(
				url,
				routingGuidesSchema.array(),
				signalOptions,
			)
		: await getAndValidate(url, routingGuidesSchema.array(), signalOptions);

	if (resp.success) {
		return resp.data;
	} else {
		throw new Error(`Failed to fetch customer routing guides: ${resp.error}`);
	}
};

export const useGetCustomerRoutingGuides = (
	customerId: number | undefined,
	isInternal: boolean,
) => {
	const resolvedId = customerId ?? 0;
	const { showError } = useNotificationActions();
	const {
		data: customerRoutingGuides,
		error: customerRoutingGuidesError,
		isLoading: customerRoutingGuidesIsLoading,
		isFetching: customerRoutingGuidesIsFetching,
		refetch: refetchCustomerRoutingGuides,
	} = useQuery({
		queryKey: ["customerRoutingGuides", resolvedId],
		queryFn: async ({ signal }: { signal: AbortSignal }) =>
			getCustomerRoutingGuides(resolvedId, isInternal, signal),
		enabled: !!customerId,
		staleTime: 0,
		gcTime: 0,
		throwOnError: false,
	});

	useEffect(() => {
		if (customerRoutingGuidesError) {
			showError(`${customerRoutingGuidesError.message}`);
		}
	}, [customerRoutingGuidesError, showError]);

	return {
		customerRoutingGuides,
		customerRoutingGuidesError,
		customerRoutingGuidesIsLoading,
		customerRoutingGuidesIsFetching,
		refetchCustomerRoutingGuides,
	};
};
